import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from './use-auth';
import { apiRequest } from '@/lib/queryClient';

// Notification interface matching the database schema
export interface Notification {
  id: number;
  tenantId: number;
  userId: number | null;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  metadata: Record<string, any> | null;
  actionUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

// Format date helper for display
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else if (today.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
    return 'Last week';
  } else {
    return date.toLocaleDateString();
  }
};

interface RenewalReminder {
  licenseId: number;
  licenseName: string;
  applicationName: string;
  renewalDate: string;
  daysUntil: number;
  cost: number | null;
  priority: 'medium' | 'high' | 'urgent';
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch tenant notifications (all notifications for the tenant)
  const {
    data: tenantNotifications = [],
    isLoading: isLoadingTenant,
    error: tenantError
  } = useQuery<Notification[]>({
    queryKey: ['/api/notifications'],
    enabled: !!user?.tenantId,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });

  // Fetch user-specific notifications
  const {
    data: userNotifications = [],
    isLoading: isLoadingUser,
    error: userError
  } = useQuery<Notification[]>({
    queryKey: ['/api/notifications/user'],
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });

  // Fetch upcoming renewal reminders (same source as "View All" page)
  const { data: renewalReminders = [] } = useQuery<RenewalReminder[]>({
    queryKey: ['/api/notifications/renewal-reminders'],
    enabled: !!user?.tenantId,
    refetchInterval: 60000,
  });

  // Map renewal reminders to Notification shape with virtual negative IDs
  const renewalNotifications: Notification[] = renewalReminders.map((r) => {
    const when =
      r.daysUntil === 0 ? 'today' :
      r.daysUntil === 1 ? 'tomorrow' :
      `in ${r.daysUntil} day${r.daysUntil === 1 ? '' : 's'}`;
    const costPart = r.cost ? ` — $${r.cost.toLocaleString()}` : '';
    return {
      id: -(r.licenseId),
      tenantId: user?.tenantId ?? 0,
      userId: null,
      type: 'license_expiry',
      title: `Renewal: ${r.applicationName}`,
      message: `"${r.licenseName}" renews ${when}${costPart}.`,
      priority: r.priority,
      isRead: false,
      metadata: { licenseId: r.licenseId, licenseName: r.licenseName, applicationName: r.applicationName, daysUntil: r.daysUntil, cost: r.cost },
      actionUrl: `/licenses/${r.licenseId}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });

  // Combine only real DB notifications (tenant + user), deduplicate by id.
  // Virtual renewalNotifications are intentionally excluded here — the scheduler and
  // check-renewals already persist real "Renewal Reminder" records to the DB. Mixing
  // virtual entries (which always have isRead: false) causes duplicates in the bell and
  // prevents "Mark all as read" from ever zeroing the unread count.
  const allNotifications = [...tenantNotifications, ...userNotifications].filter(
    (notification, index, self) =>
      index === self.findIndex(n => n.id === notification.id)
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return apiRequest(`/api/notifications/${notificationId}/mark-read`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
    },
  });

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async (userId?: number) => {
      return apiRequest('/api/notifications/mark-all-read', {
        method: 'POST',
        body: { userId },
      });
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return apiRequest(`/api/notifications/${notificationId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
    },
  });

  // Create notification mutation (for admin use)
  const createNotificationMutation = useMutation({
    mutationFn: async (notification: {
      userId?: number;
      type: string;
      title: string;
      message: string;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      metadata?: Record<string, any>;
      actionUrl?: string;
    }) => {
      return apiRequest('/api/notifications', {
        method: 'POST',
        body: JSON.stringify(notification),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
    },
  });

  // Mark a notification as read
  const markAsRead = (id: number) => {
    markAsReadMutation.mutate(id);
  };

  // Mark all notifications as read
  const markAllAsRead = (userId?: number) => {
    markAllAsReadMutation.mutate(userId);
  };

  // Delete a notification
  const deleteNotification = (id: number) => {
    deleteNotificationMutation.mutate(id);
  };

  // Trigger renewal check once per day across all tabs (localStorage is shared).
  // sessionStorage is tab-isolated, so multiple open tabs would each fire the request
  // simultaneously, racing past the server-side 24 h dedup and inserting duplicate records.
  useEffect(() => {
    if (!user?.tenantId) return;
    const key = `renewal_check_${user.tenantId}_${new Date().toDateString()}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    apiRequest('/api/notifications/check-renewals', { method: 'POST' })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
      })
      .catch(() => {}); // silent failure
  }, [user?.tenantId]);

  // Create a new notification
  const createNotification = (notification: {
    userId?: number;
    type: string;
    title: string;
    message: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    metadata?: Record<string, any>;
    actionUrl?: string;
  }) => {
    createNotificationMutation.mutate(notification);
  };

  // Transform notifications for display (add formatted date)
  const notifications = allNotifications.map(notification => ({
    ...notification,
    formattedDate: formatDate(notification.createdAt),
  }));

  // Get unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Get unread notifications only
  const unreadNotifications = notifications.filter(n => !n.isRead);

  return {
    notifications,
    unreadNotifications,
    unreadCount,
    isLoading: isLoadingTenant || isLoadingUser,
    error: tenantError || userError,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
    isCreating: createNotificationMutation.isPending,
  };
}
